import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import {
  PopoverSelect,
  type PopoverSelectOption,
  type PopoverSelectTriggerState,
} from '@rocketlane/ui';
import { Meeting } from 'api/meeting/types';
import moment from 'moment';
import { useGetAllMeetingsQueryWithScrollPagination } from 'queries/meeting';
import { useCallback, useEffect, useMemo, useState } from 'react';

const DEFAULT_LIST_WIDTH = 700;
const LIST_HEIGHT = 400;
const PAGE_LIMIT = 25;
const SEARCH_DEBOUNCE_MS = 300;

const describeParticipants = (meeting: Meeting) => {
  const participants = meeting.meta?.participants ?? [];

  if (participants.length === 0) {
    return 'No participants';
  }

  const [first, ...rest] = participants;
  const name = first.firstName || first.emailId;

  if (rest.length === 0) {
    return name;
  }

  return `${name} and ${rest.length} ${rest.length === 1 ? 'other' : 'others'}`;
};

const describeMeeting = (meeting: Meeting) =>
  `${moment(meeting.startTime).format('D MMM h:mmA')} • ${describeParticipants(
    meeting,
  )}`;

export const MeetingList = ({
  meetings: externalMeetings,
  isLoading: externalIsLoading,
  isDropdownOpen,
  onOpenChange,
  selectedMeetingId,
  handleMenuClick,
  filter,
  listWidth = DEFAULT_LIST_WIDTH,
  renderTrigger,
  searchable = true,
  searchPlaceholder = 'Search meetings',
}: {
  meetings?: Meeting[];
  isLoading: boolean;
  selectedMeetingId: number | undefined;
  isDropdownOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  handleMenuClick: (meetingId: number, meeting: Meeting) => void;
  filter?: GroupedFilter;
  listWidth?: number;
  renderTrigger: (state: PopoverSelectTriggerState) => React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const trimmedSearchTerm = searchTerm.trim();

  // the package clears its search box on close but only reports changes while open
  useEffect(() => {
    if (!isDropdownOpen) {
      setSearchTerm('');
    }
  }, [isDropdownOpen]);
  const {
    meetings: paginatedMeetings,
    isLoading: isPaginatedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAllMeetingsQueryWithScrollPagination(filter, {
    enabled: isDropdownOpen || false,
    limit: PAGE_LIMIT,
    searchTerm: trimmedSearchTerm || undefined,
  });

  const meetingList = useMemo(() => {
    const meetings = externalMeetings || paginatedMeetings || [];

    if (!externalMeetings || !trimmedSearchTerm) {
      return meetings;
    }

    const query = trimmedSearchTerm.toLowerCase();

    return meetings.filter((meeting) =>
      meeting.meetingName.toLowerCase().includes(query),
    );
  }, [externalMeetings, paginatedMeetings, trimmedSearchTerm]);

  const meetingsById = useMemo(
    () => new Map(meetingList.map((meeting) => [meeting.meetingId, meeting])),
    [meetingList],
  );

  const options = useMemo<PopoverSelectOption[]>(
    () =>
      meetingList.map((meeting) => ({
        value: meeting.meetingId,
        label: meeting.meetingName,
        description: describeMeeting(meeting),
      })),
    [meetingList],
  );

  const value = useMemo(
    () => options.find((option) => option.value === selectedMeetingId) ?? null,
    [options, selectedMeetingId],
  );

  const onChange = useCallback(
    (next: PopoverSelectOption | PopoverSelectOption[] | null) => {
      const selected = Array.isArray(next) ? next[0] : next;
      const meeting = selected
        ? meetingsById.get(Number(selected.value))
        : undefined;

      if (meeting) {
        handleMenuClick(meeting.meetingId, meeting);
      }
    },
    [handleMenuClick, meetingsById],
  );

  return (
    <PopoverSelect
      className="w-full"
      options={options}
      value={value}
      onChange={onChange}
      renderTrigger={renderTrigger}
      open={isDropdownOpen}
      onOpenChange={onOpenChange}
      searchable={searchable}
      searchPlaceholder={searchPlaceholder}
      onSearchChange={searchable ? setSearchTerm : undefined}
      searchDebounceMs={SEARCH_DEBOUNCE_MS}
      hasMore={hasNextPage}
      onLoadMore={fetchNextPage}
      isLoading={externalIsLoading || isPaginatedLoading || isFetchingNextPage}
      noOptionsMessage="No meetings"
      dropdownWidth={listWidth}
      maxListHeight={LIST_HEIGHT}
    />
  );
};
